/**
 * SDK Channel Manager for iframe
 *
 * Manages secure communication channels between iframe and parent SDK applications.
 * Each channel represents an authenticated connection from a parent origin.
 *
 * NOTE: This is NOT a user session (login). This is the SDK communication channel
 * that allows the parent SDK to communicate securely with the iframe.
 */

import type { SdkChannel } from './types';

export class SdkChannelManager {
  private channels: Map<string, SdkChannel> = new Map();
  private readonly CHANNEL_TIMEOUT = 30 * 60 * 1000; // 30 minutes

  constructor() {
    // Cleanup expired channels periodically
    setInterval(() => this.cleanupExpiredChannels(), 5 * 60 * 1000); // Every 5 minutes
  }

  /**
   * Create a new SDK channel
   */
  /** @param sdkVersion announced by the parent in SDK_AUTH; absent for SDKs that predate iframe sessions */
  createChannel(projectId: string, origin: string, sdkVersion?: string): string {
    const channelToken = this.generateSecureToken();

    const channel: SdkChannel = {
      token: channelToken,
      projectId,
      origin,
      ...(sdkVersion ? { sdkVersion } : {}),
      createdAt: Date.now(),
      lastActivity: Date.now(),
    };

    this.channels.set(channelToken, channel);

    console.log(`[iframe][SdkChannel] ✓ Created: ${channelToken.slice(0, 16)}... origin=${origin}, total=${this.channels.size}`);

    return channelToken;
  }

  /**
   * Validate channel token and origin
   */
  validateChannel(channelToken: string, origin: string): boolean {
    if (!channelToken) {
      console.warn('[iframe][SdkChannel] ✗ channelToken is null/undefined');
      return false;
    }

    const channel = this.channels.get(channelToken);

    if (!channel) {
      console.warn(`[iframe][SdkChannel] ✗ NOT FOUND: ${channelToken.slice(0, 16)}... (active channels: ${this.channels.size})`);
      return false;
    }

    // Check origin matches
    if (channel.origin !== origin) {
      console.error(`[iframe][SdkChannel] ✗ ORIGIN MISMATCH: expected ${channel.origin}, got ${origin}`);
      return false;
    }

    // Check channel timeout
    const age = Date.now() - channel.lastActivity;
    if (age > this.CHANNEL_TIMEOUT) {
      console.warn(`[iframe][SdkChannel] ✗ EXPIRED: age=${Math.round(age / 1000)}s > timeout=${this.CHANNEL_TIMEOUT / 1000}s`);
      this.channels.delete(channelToken);
      return false;
    }

    // Update last activity
    channel.lastActivity = Date.now();

    return true;
  }

  /**
   * Get channel by token
   */
  getChannel(channelToken: string | undefined): SdkChannel | null {
    if (!channelToken) return null;
    return this.channels.get(channelToken) || null;
  }

  /**
   * Invalidate channel
   */
  invalidateChannel(channelToken: string): void {
    const channel = this.channels.get(channelToken);
    if (channel) {
      console.log(`[iframe][SdkChannel] Invalidated channel for origin: ${channel.origin}`);
      this.channels.delete(channelToken);
    }
  }

  /**
   * Get all channels for a project
   */
  getProjectChannels(projectId: string): SdkChannel[] {
    return Array.from(this.channels.values()).filter(
      (channel) => channel.projectId === projectId
    );
  }

  /**
   * Generate cryptographically secure channel token
   */
  private generateSecureToken(): string {
    const buffer = new Uint8Array(32);
    crypto.getRandomValues(buffer);
    return Array.from(buffer, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Cleanup expired channels
   */
  private cleanupExpiredChannels(): void {
    const now = Date.now();
    let cleanedCount = 0;

    for (const [token, channel] of this.channels.entries()) {
      const age = now - channel.lastActivity;
      if (age > this.CHANNEL_TIMEOUT) {
        this.channels.delete(token);
        cleanedCount++;
      }
    }

    if (cleanedCount > 0) {
      console.log(`[iframe][SdkChannel] Cleaned up ${cleanedCount} expired channels`);
    }
  }

  /**
   * Check if there is an active channel for a given origin
   */
  hasChannel(origin: string): boolean {
    const now = Date.now();

    for (const channel of this.channels.values()) {
      if (channel.origin === origin) {
        // Check if channel is not expired
        const age = now - channel.lastActivity;
        if (age <= this.CHANNEL_TIMEOUT) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Get channel statistics
   */
  getStats(): { total: number; byProject: Record<string, number> } {
    const stats: { total: number; byProject: Record<string, number> } = {
      total: this.channels.size,
      byProject: {},
    };

    for (const channel of this.channels.values()) {
      stats.byProject[channel.projectId] = (stats.byProject[channel.projectId] || 0) + 1;
    }

    return stats;
  }
}
